/**
 * Thẻ một dòng lịch sử ví trong drawer "Chi tiết điểm thưởng" (`DetailBonusPoint.js` >
 * `BonusPointHistory.js`) trên điện thoại/iPad (Figma FR-W-10 responsive, 14/09/2026) — thay bảng
 * khi `useIsCompact()` (≤1199px). Cùng nội dung + cách định dạng như một dòng bảng desktop.
 *
 * Năm hàm hiển thị DÙNG CHUNG với cột bảng được export ở đây (`pointHistoryPointText`,
 * `pointHistoryDetailText`, `pointHistoryAmountText`, `pointHistoryStatusTag`) — cột bảng
 * (`BonusPointHistory.js`) gọi lại NGUYÊN các hàm này, không chép logic.
 */
import React, { memo } from 'react'
import moment from 'moment'

import {
  MobileCard, MobileCardField, MobileCardFields,
} from '../common/MobileCard'
import { POINT_HISTORY_COLUMN_LABEL } from '../../constants/bonusPointLabels'
import { BONUS_POINT_ACTION, BONUS_POINT_STATUS } from '../../constants/constant'
import { formatNumber } from '../../util'

/** Phần dữ liệu một dòng lịch sử ví mà thẻ/cột bảng cần — HẸP đúng phần dùng, KHÔNG phải hợp đồng BE. */
export interface PointHistoryItem {
  id: string
  created_at: string
  transaction_type: string
  note: string
  updated_user?: string
  point: number | string
  transaction_status: string
}

interface PointHistoryCardProps {
  item: PointHistoryItem
  /** Chỉ tab "Đổi điểm" mới hiện ô "Thành tiền" — đúng cột `amount` của bảng bị lọc ẩn ở tab "Biến động điểm". */
  showAmount: boolean
}

interface BonusPointActionMeta {
  name: string
  prefix: string
  text_color_class: string
}

// `BONUS_POINT_ACTION`/`BONUS_POINT_STATUS` (`constants/constant.js`) là object literal JS — TS suy kiểu ra đúng
// tập key cố định hiện có, tra bằng `transaction_type`/`transaction_status` (kiểu `string` bất kỳ từ API) thì lỗi
// "Element implicitly has an 'any' type". Ép qua `Record<string, ... | undefined>` (đúng type thật: key lạ ⇒ `undefined`).
const bonusPointActionMap = BONUS_POINT_ACTION as Record<string, BonusPointActionMeta | undefined>
const bonusPointStatusMap = BONUS_POINT_STATUS as Record<string, string | undefined>

/**
 * Ô "Số điểm": tiền tố (`+`/`-`/rỗng) + số đã format + class màu — DÙNG CHUNG giữa cột bảng và thẻ.
 */
export const pointHistoryPointText = (item: PointHistoryItem): { className: string; text: string } => {
  const meta = bonusPointActionMap[item.transaction_type]
  return {
    className: meta?.text_color_class ?? '',
    text: `${meta?.prefix ?? ''}${formatNumber(Number(item.point))}`,
  }
}

/**
 * Ô "Chi tiết": ghép tên người sửa vào câu ghi chú — giữ NGUYÊN hành vi cột bảng cũ
 * (`note.replace('Admin', record?.updated_user)`), kể cả khi `updated_user` không có (ra literal
 * "undefined" — quirk có sẵn từ trước, KHÔNG sửa ở task này vì không được đổi hành vi hiện có).
 */
export const pointHistoryDetailText = (item: PointHistoryItem): string => (
  // `String(...)` nói thẳng quirk trên: `replace` vốn tự ép `undefined` thành chữ "undefined", kết quả y hệt code cũ.
  item.note.replace('Admin', String(item.updated_user))
)

/** Ô "Thành tiền" (chỉ tab "Đổi điểm") — DÙNG CHUNG giữa cột bảng và thẻ. */
export const pointHistoryAmountText = (item: PointHistoryItem): string => formatNumber(Number(item.point) * 100)

/**
 * Tag trạng thái giao dịch — DÙNG CHUNG giữa cột "Trạng thái" của bảng desktop và thẻ mobile/iPad.
 * Giữ NGUYÊN cấu trúc DOM cũ của cột bảng (`tag-wrapper` > `tag tag-<status>`).
 */
export const pointHistoryStatusTag = (status: string): JSX.Element => (
  <div className="tag-wrapper">
    <div className={`tag tag-${status.toLocaleLowerCase()}`}>
      {bonusPointStatusMap[status]}
    </div>
  </div>
)

const PointHistoryCard: React.FC<PointHistoryCardProps> = ({ item, showAmount }) => {
  const pointText = pointHistoryPointText(item)
  return (
    <MobileCard
      title={bonusPointActionMap[item.transaction_type]?.name}
      aside={pointHistoryStatusTag(item.transaction_status)}
      divided
    >
      <MobileCardFields>
        <MobileCardField label={POINT_HISTORY_COLUMN_LABEL.time}>
          {moment(item.created_at).format('DD/MM/YYYY')}
        </MobileCardField>
        <MobileCardField label={POINT_HISTORY_COLUMN_LABEL.point}>
          <span className={pointText.className}>{pointText.text}</span>
        </MobileCardField>
        <MobileCardField label={POINT_HISTORY_COLUMN_LABEL.detail} wide>
          {pointHistoryDetailText(item)}
        </MobileCardField>
        {showAmount && (
          <MobileCardField label={POINT_HISTORY_COLUMN_LABEL.amount}>
            {pointHistoryAmountText(item)}
          </MobileCardField>
        )}
      </MobileCardFields>
    </MobileCard>
  )
}

export default memo(PointHistoryCard)
